/* eslint-disable @typescript-eslint/no-explicit-any */
import { Typography, type FormInstance, type TableColumnType } from 'antd'
import type { AnyObject } from 'antd/es/_util/type'
import { isPlainObject, merge } from 'lodash-es'
import { useRef } from 'react'
import type { QueryListPayload, QueryListRef } from 'react-toolkits'
import { QueryList } from 'react-toolkits'
import { formSchema } from '../schemas'
import { queryListSchema } from '../schemas/query-list'
import { renderSchemaArray, replaceValueWithJMESPath } from '../utils'
import { withParsedData } from './base'
import { FormRenderer } from './form'

type FormValues = AnyObject

interface ListResponse {
  rows: any[]
  total: number
}

export const QueryListRenderer = withParsedData(queryListSchema)(({ parsedData }) => {
  const { storeId, fields, initialValues, formLayout, columns, params, body, footer, extra, gutter, actions, ...rest } =
    parsedData
  const ref = useRef<QueryListRef>(null)
  const newColumns: (TableColumnType<any> & { tpl?: string })[] = columns ? [...columns] : []

  // 处理对象字面量或 JSON 字符串
  newColumns.forEach(column => {
    column.render = (value: any) => {
      if (isPlainObject(value)) {
        return (
          <Typography.Paragraph style={{ maxHeight: 200, overflow: 'auto' }}>
            <pre>{JSON.stringify(value, null, 2)}</pre>
          </Typography.Paragraph>
        )
      } else if (typeof value === 'string') {
        try {
          const obj = JSON.parse(value)
          if (isPlainObject(obj)) {
            return (
              <Typography.Paragraph style={{ maxHeight: 200, overflow: 'auto' }}>
                <pre>{JSON.stringify(obj, null, 2)}</pre>
              </Typography.Paragraph>
            )
          }
          return value
        } catch (error) {
          return value
        }
      } else {
        return value
      }
    }
  })

  if (actions) {
    newColumns.push({
      title: '操作',
      align: 'center',
      render: (record: any) => renderSchemaArray(actions, record),
    })
  }

  const renderForm = (form: FormInstance) => {
    const data = formSchema.parse({
      type: 'form',
      fields,
      initialValues,
      layout: formLayout,
      gutter,
    })
    return <FormRenderer data={data} form={form} externalStoreId={storeId} />
  }

  const renderFooter = footer && (() => renderSchemaArray(footer, ref.current?.data))

  const getDataSource = (data?: ListResponse) => data?.rows
  const getTotal = (data?: ListResponse) => data?.total

  const getParams = ({ page, size, formValue }: QueryListPayload) => {
    if (params) {
      return merge(
        {
          page,
          size,
        },
        replaceValueWithJMESPath(formValue, params),
      )
    } else {
      return {
        ...formValue,
        page,
        size,
      }
    }
  }

  const getBody = ({ page, size, formValue }: QueryListPayload) => {
    if (body) {
      return merge(
        {
          page,
          size,
        },
        replaceValueWithJMESPath(formValue, body),
      )
    } else {
      return {
        ...formValue,
        page,
        size,
      }
    }
  }

  const tableExtra = renderSchemaArray(extra)

  return (
    <QueryList<any, FormValues, ListResponse>
      {...rest}
      ref={ref}
      columns={newColumns}
      renderForm={renderForm}
      footer={renderFooter}
      getDataSource={getDataSource}
      getTotal={getTotal}
      params={getParams}
      body={getBody}
      tableExtra={tableExtra}
    />
  )
})
